import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import ProtectedRoute from "./components/ProtectedRoute";
import CustomerRoute from "./components/CustomerRoute";
import Layout from "./components/Layout";
import PortalLayout from "./components/PortalLayout";
import { APP_TITLE } from "./lib/version";

// Public pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import PasswordResetRequest from "./pages/PasswordResetRequest";
import PasswordResetConfirm from "./pages/PasswordResetConfirm";
import VerifyEmail from "./pages/VerifyEmail";
import Unsubscribe from "./pages/Unsubscribe";

// Internal (Admin/Employee) pages
import Dashboard from "./pages/Dashboard";
import Produkte from "./pages/Produkte";
import Lagerbestand from "./pages/Lagerbestand";
import Wareneingang from "./pages/Wareneingang";
import Warenausgang from "./pages/Warenausgang";
import Umlagerung from "./pages/Umlagerung";
import Buchungshistorie from "./pages/Buchungshistorie";
import ListenBelege from "./pages/ListenBelege";
import Lagerorte from "./pages/Lagerorte";
import Lieferanten from "./pages/Lieferanten";
import Automaten from "./pages/Automaten";
import Integrationen from "./pages/Integrationen";
import POS from "./pages/POS";
import Kunden from "./pages/Kunden";
import Kassen from "./pages/Kassen";
import Archiv from "./pages/Archiv";
import Benutzer from "./pages/Benutzer";
import AdminKundenportal from "./pages/AdminKundenportal";
import Einstellungen from "./pages/Einstellungen";

// Customer portal pages
import PortalHome from "./pages/portal/PortalHome";
import PortalAccount from "./pages/portal/PortalAccount";
import PortalNews from "./pages/portal/PortalNews";
import PortalLocations from "./pages/portal/PortalLocations";
import PortalPurchases from "./pages/portal/PortalPurchases";
import PortalImpressum from "./pages/portal/PortalImpressum";
import PortalDatenschutz from "./pages/portal/PortalDatenschutz";
import PortalAGB from "./pages/portal/PortalAGB";
import { SystemSettingsProvider } from "./lib/systemSettings";
import { PosLockProvider } from "./lib/posLock";

export default function App() {
  // Browser-Tab-Titel aus zentraler Version — einmalig beim Mount setzen.
  // Sichtbarer Wert z. B. „Inventory (V1.0.0)". Bei Release nur
  // ``frontend/package.json`` -> ``version`` anheben.
  useEffect(() => { document.title = APP_TITLE; }, []);
  return (
    <AuthProvider>
      <SystemSettingsProvider>
      <PosLockProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/password-reset-request" element={<PasswordResetRequest />} />
          <Route path="/password-reset" element={<PasswordResetConfirm />} />
          <Route path="/reset-password" element={<PasswordResetConfirm />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/unsubscribe/:token" element={<Unsubscribe />} />

          {/* Customer portal */}
          <Route
            path="/portal"
            element={<CustomerRoute><PortalLayout /></CustomerRoute>}
          >
            <Route index element={<PortalHome />} />
            <Route path="account" element={<PortalAccount />} />
            <Route path="news" element={<PortalNews />} />
            <Route path="locations" element={<PortalLocations />} />
            <Route path="purchases" element={<PortalPurchases />} />
            {/* Legacy /portal/club → Übersicht (QR-Code lebt jetzt dort). */}
            <Route path="club" element={<Navigate to="/portal" replace />} />
            <Route path="impressum" element={<PortalImpressum />} />
            <Route path="datenschutz" element={<PortalDatenschutz />} />
            <Route path="agb" element={<PortalAGB />} />
          </Route>

          {/* Internal (Admin/Employee) */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="produkte" element={<Produkte />} />
            <Route path="lagerbestand" element={<Lagerbestand />} />
            <Route path="wareneingang" element={<Wareneingang />} />
            <Route path="warenausgang" element={<Warenausgang />} />
            <Route path="umlagerung" element={<Umlagerung />} />
            <Route path="buchungshistorie" element={<Buchungshistorie />} />
            <Route path="listen-belege" element={<ListenBelege />} />
            <Route path="lagerorte" element={<Lagerorte />} />
            <Route path="lieferanten" element={<Lieferanten />} />
            <Route path="automaten" element={<Automaten />} />
            <Route path="integrationen" element={<Integrationen />} />
            <Route path="pos" element={<POS />} />
            <Route path="kunden" element={<Kunden />} />
            <Route path="kassen" element={<Kassen />} />
            <Route path="archiv" element={<Archiv />} />
            <Route path="benutzer" element={<Benutzer />} />
            <Route path="kundenportal" element={<AdminKundenportal />} />
            {/* Bewusst weiterleitend: Der frühere Top-Level-„Newsletter"
                lebt jetzt als Tab unter /kundenportal. Alter Deep-Link
                bleibt kompatibel. */}
            <Route path="newsletter" element={<Navigate to="/kundenportal?tab=newsletter" replace />} />
            <Route path="einstellungen" element={<Einstellungen />} />
          </Route>

          {/* Catch-all → Dashboard/Portal statt weißer Seite. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="top-center" richColors closeButton />
      </PosLockProvider>
      </SystemSettingsProvider>
    </AuthProvider>
  );
}
